import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBrand } from '@/shared/brand/useBrand';
import { BrandLogo } from '@/shared/components/BrandLogo';
import { LocaleSwitcher } from '@/shared/i18n/LocaleSwitcher';
import { useI18n } from '@/shared/i18n/useT';
import type { MessageKey } from '@/shared/i18n/messages';

/**
 * The public face of docs/AI-INTEGRATION.md. The repository is private, so the
 * "View integration docs" action on /ai points here instead of at GitHub.
 *
 * Everything here is deliberately credential-free: the page is served to
 * signed-out readers, so tokens appear as placeholders and the reader is sent
 * to /ai to copy their own.
 */

const TOOL_SIGNATURES: readonly { readonly name: string; readonly desc: MessageKey; readonly params: string }[] = [
  {
    name: 'numtable_list_documents',
    desc: 'mcp.tools.listDocuments',
    params: "folderId?: string · kind?: 'folder' | 'document' | 'all' · docType?: string · search?: string · includeTrash?: boolean",
  },
  { name: 'numtable_get_document', desc: 'mcp.tools.getDocument', params: 'documentId: string' },
  {
    name: 'numtable_read_rows',
    desc: 'mcp.tools.readRows',
    params: 'documentId: string · tabId?: string · limit?: number (max 1000) · offset?: number · filterColumnId?: string · filterValue?: string | number | boolean',
  },
  {
    name: 'numtable_append_rows',
    desc: 'mcp.tools.appendRows',
    params: 'documentId: string · tabId?: string · rows: Array<Record<string, unknown>>',
  },
  {
    name: 'numtable_update_rows',
    desc: 'mcp.tools.updateRows',
    params: 'documentId: string · tabId?: string · updates: Array<{ rowId: string; data: object }>',
  },
  {
    name: 'numtable_configure_columns',
    desc: 'mcp.tools.configureColumns',
    params: 'documentId: string · tabId?: string · columns: Array<{ columnId: string; format?: object | null; formula?: string | null }>',
  },
  {
    name: 'numtable_create_sheet',
    desc: 'mcp.tools.createSheet',
    params: 'name: string · folderId?: string · tabName?: string · columns?: Array<{ id, name, cellType?, width? }> · initialRows?: number',
  },
];

const REST_ENDPOINTS: readonly { readonly method: string; readonly path: string; readonly desc: MessageKey }[] = [
  { method: 'GET', path: '/mcp/tools', desc: 'docs.ai.rest.tools' },
  { method: 'GET', path: '/mcp/documents', desc: 'docs.ai.rest.documents' },
  { method: 'GET', path: '/mcp/documents/:id', desc: 'docs.ai.rest.document' },
  { method: 'GET', path: '/mcp/documents/:id/rows', desc: 'docs.ai.rest.readRows' },
  { method: 'POST', path: '/mcp/documents/:id/rows', desc: 'docs.ai.rest.appendRows' },
  { method: 'PATCH', path: '/mcp/documents/:id/rows', desc: 'docs.ai.rest.updateRows' },
  { method: 'POST', path: '/mcp/documents', desc: 'docs.ai.rest.createSheet' },
];

export function AiDocsPage() {
  const { t } = useI18n();
  const brand = useBrand();
  const title = t('docs.ai.title');
  const description = t('docs.ai.description');

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} · ${brand.name}`;

    const descriptionMeta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = descriptionMeta?.content;
    if (descriptionMeta) descriptionMeta.content = description;

    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const previousCanonical = canonical?.href;
    if (canonical) {
      const canonicalUrl = new URL(window.location.href);
      canonicalUrl.search = '';
      canonicalUrl.hash = '';
      canonical.href = canonicalUrl.toString();
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta && previousDescription !== undefined) descriptionMeta.content = previousDescription;
      if (canonical && previousCanonical !== undefined) canonical.href = previousCanonical;
    };
  }, [brand.name, description, title]);

  // The origin the reader is on is the origin their agent must call, which
  // keeps the snippets correct on preview deployments and on localhost.
  const origin = typeof window !== 'undefined' ? window.location.origin : `https://${brand.host}`;
  const endpoint = `${origin}/mcp`;

  const httpConfig = [
    '{',
    '  "mcpServers": {',
    '    "numtable": {',
    `      "url": "${endpoint}",`,
    '      "headers": {',
    '        "Authorization": "Bearer <YOUR_TOKEN>",',
    '        "x-org-id": "<OPTIONAL_ORG_UUID>"',
    '      }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  const sseConfig = [
    '{',
    '  "mcpServers": {',
    '    "numtable": {',
    '      "type": "sse",',
    `      "url": "${endpoint}",`,
    '      "headers": {',
    '        "Authorization": "Bearer <YOUR_TOKEN>"',
    '      }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  const curlExample = [
    `curl -X POST "${endpoint}" \\`,
    '  -H "Authorization: Bearer <YOUR_TOKEN>" \\',
    '  -H "x-org-id: <OPTIONAL_ORG_UUID>" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list","params":{}}'`,
  ].join('\n');

  return (
    <div className="-mx-3 min-h-[calc(100dvh-3rem)] bg-surface sm:-mx-6">
      <header className="border-b border-border bg-surface-2">
        <div className="mx-auto flex min-h-header max-w-4xl items-center justify-between gap-4 px-3 sm:px-6">
          <Link to="/" className="tap-target flex items-center rounded-token" aria-label={brand.name}>
            <BrandLogo className="h-6 w-auto sm:h-7" />
          </Link>
          <div className="flex items-center gap-2">
            <LocaleSwitcher compact />
            <Link
              to="/ai"
              className="tap-target inline-flex items-center rounded-token px-3 py-2 text-sm font-medium text-accent hover:bg-accent-soft"
            >
              {t('docs.ai.openInApp')}
            </Link>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-3 py-10 sm:px-6 sm:py-14">
        <article className="max-w-3xl">
          <p className="text-sm font-medium text-accent">{brand.name}</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-fg sm:text-4xl">{title}</h1>
          <p className="mt-4 text-base leading-7 text-muted">{t('docs.ai.intro')}</p>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-fg">{t('docs.ai.connection.title')}</h2>
            <p className="mt-3 text-base leading-7 text-muted">{t('docs.ai.connection.body')}</p>
            <dl className="mt-5 grid gap-x-6 gap-y-3 sm:grid-cols-[max-content_1fr]">
              <dt className="text-sm font-medium text-fg">{t('docs.ai.connection.endpointLabel')}</dt>
              <dd><code className="break-all font-mono text-sm text-accent">{`POST ${endpoint}`}</code></dd>
              <dt className="text-sm font-medium text-fg">{t('docs.ai.connection.authLabel')}</dt>
              <dd><code className="break-all font-mono text-sm text-accent">Authorization: Bearer &lt;YOUR_TOKEN&gt;</code></dd>
              <dt className="text-sm font-medium text-fg">{t('docs.ai.connection.orgLabel')}</dt>
              <dd><code className="break-all font-mono text-sm text-accent">x-org-id: &lt;ORG_UUID&gt;</code></dd>
            </dl>
            <p className="mt-4 text-sm leading-6 text-muted">{t('docs.ai.connection.tokenNote')}</p>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-fg">{t('docs.ai.quickstart.title')}</h2>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-base leading-7 text-muted">
              <li>{t('docs.ai.quickstart.step1')}</li>
              <li>{t('docs.ai.quickstart.step2')}</li>
              <li>{t('docs.ai.quickstart.step3')}</li>
            </ol>

            <h3 className="mt-8 text-base font-semibold text-fg">{t('docs.ai.quickstart.httpTitle')}</h3>
            <p className="mt-2 text-sm leading-6 text-muted">{t('docs.ai.quickstart.httpBody')}</p>
            <CodeBlock code={httpConfig} />

            <h3 className="mt-8 text-base font-semibold text-fg">{t('docs.ai.quickstart.sseTitle')}</h3>
            <p className="mt-2 text-sm leading-6 text-muted">{t('docs.ai.quickstart.sseBody')}</p>
            <CodeBlock code={sseConfig} />

            <h3 className="mt-8 text-base font-semibold text-fg">{t('docs.ai.quickstart.curlTitle')}</h3>
            <p className="mt-2 text-sm leading-6 text-muted">{t('docs.ai.quickstart.curlBody')}</p>
            <CodeBlock code={curlExample} />
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-fg">{t('docs.ai.tools.title')}</h2>
            <p className="mt-3 text-base leading-7 text-muted">{t('docs.ai.tools.body')}</p>
            <div className="mt-5 space-y-3">
              {TOOL_SIGNATURES.map((tool) => (
                <div key={tool.name} className="rounded-token border border-border bg-surface-2 p-4">
                  <code className="block font-mono text-sm font-semibold text-accent">{tool.name}</code>
                  <p className="mt-1 text-sm leading-6 text-muted">{t(tool.desc)}</p>
                  <p className="mt-2 break-words font-mono text-xs leading-5 text-muted">{tool.params}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-fg">{t('docs.ai.rest.title')}</h2>
            <p className="mt-3 text-base leading-7 text-muted">{t('docs.ai.rest.body')}</p>
            <ul className="mt-5 space-y-2">
              {REST_ENDPOINTS.map((row) => (
                <li key={`${row.method} ${row.path}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <code className="font-mono text-xs font-semibold text-accent">{row.method}</code>
                  <code className="break-all font-mono text-sm text-fg">{row.path}</code>
                  <span className="text-sm text-muted">{t(row.desc)}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-fg">{t('docs.ai.security.title')}</h2>
            <p className="mt-3 whitespace-pre-line text-base leading-7 text-muted">{t('docs.ai.security.body')}</p>
          </section>

          <div className="mt-10 rounded-token border border-border bg-surface-2 p-5 sm:p-6">
            <h2 className="text-xl font-semibold text-fg">{t('docs.ai.help.title')}</h2>
            <p className="mt-3 text-base leading-7 text-muted">{t('docs.ai.help.body')}</p>
            <a
              href={`mailto:support@${brand.host}`}
              className="mt-3 inline-flex min-h-11 items-center rounded-token text-sm font-medium text-accent underline decoration-accent/40 underline-offset-4 hover:text-accent-hover"
            >
              {`support@${brand.host}`}
            </a>
          </div>
        </article>
      </div>

      <footer className="border-t border-border bg-surface-2">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center gap-x-4 gap-y-2 px-3 py-5 text-sm text-muted sm:px-6">
          <span>{t('legal.footer')}</span>
          <Link to="/privacy" className="text-accent hover:text-accent-hover">{t('legal.privacy.shortTitle')}</Link>
          <Link to="/terms" className="text-accent hover:text-accent-hover">{t('legal.terms.shortTitle')}</Link>
        </div>
      </footer>
    </div>
  );
}

function CodeBlock({ code }: { readonly code: string }) {
  return (
    <pre className="mt-3 overflow-x-auto rounded-token border border-border bg-surface-2 p-4 font-mono text-xs leading-relaxed text-fg">
      {code}
    </pre>
  );
}
